import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    RefreshCw,
    Send,
    MessageSquare,
    Volume2,
    Pause,
    RotateCcw,
    CheckCircle,
    XCircle,
} from "lucide-react";
import { AnimatePresence, motion, MotionConfig, Reorder } from "framer-motion";
import useMeasure from "react-use-measure";
import { api } from "../../lib/api";
import { toast } from "sonner";
import MindMap from "../../components/MindMap";

// ─── Direction Aware Tabs (Cult UI) ──────────────────────────────────────────
function DirectionAwareTabs({ tabs, onChange }) {
    const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 0);
    const [direction, setDirection] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [ref, bounds] = useMeasure();

    const content = useMemo(
        () => tabs.find((t) => t.id === activeTab)?.content ?? null,
        [activeTab, tabs]
    );

    const handleTabClick = (newId) => {
        if (newId !== activeTab && !isAnimating) {
            setDirection(newId > activeTab ? 1 : -1);
            setActiveTab(newId);
            onChange?.(newId);
        }
    };

    const variants = {
        initial: (dir) => ({ x: 300 * dir, opacity: 0, filter: "blur(4px)" }),
        active:  { x: 0, opacity: 1, filter: "blur(0px)" },
        exit:    (dir) => ({ x: -300 * dir, opacity: 0, filter: "blur(4px)" }),
    };

    return (
        <div className="flex flex-col items-center w-full">
            <div className="flex space-x-1 rounded-full cursor-pointer bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 px-[3px] py-[3px] shadow-inner w-full">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        style={{ WebkitTapHighlightColor: "transparent" }}
                        className={`relative flex-1 rounded-full px-3 py-2.5 text-xs sm:text-sm font-medium transition-colors focus-visible:outline-none flex flex-col items-center gap-0.5 ${
                            activeTab === tab.id ? "text-white" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        }`}
                    >
                        {activeTab === tab.id && (
                            <motion.span
                                layoutId="style-bubble"
                                className={`absolute inset-0 z-10 rounded-full ${tab.bubbleColor} border border-white/10`}
                                transition={{ type: "spring", bounce: 0.19, duration: 0.4 }}
                            />
                        )}
                        <span className="relative z-20 text-base leading-none">{tab.emoji}</span>
                        <span className="relative z-20 font-semibold text-[11px] leading-none">{tab.label}</span>
                        <span className="relative z-20 text-[9px] text-slate-500 dark:text-slate-400 leading-none hidden sm:block">{tab.desc}</span>
                    </button>
                ))}
            </div>
            <MotionConfig transition={{ duration: 0.35, type: "spring", bounce: 0.18 }}>
                <motion.div
                    className="relative w-full overflow-hidden mt-4"
                    initial={false}
                    animate={{ height: bounds.height }}
                >
                    <div ref={ref}>
                        <AnimatePresence
                            custom={direction}
                            mode="popLayout"
                            onExitComplete={() => setIsAnimating(false)}
                        >
                            <motion.div
                                key={activeTab}
                                variants={variants}
                                initial="initial"
                                animate="active"
                                exit="exit"
                                custom={direction}
                                onAnimationStart={() => setIsAnimating(true)}
                                onAnimationComplete={() => setIsAnimating(false)}
                            >
                                {content}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </motion.div>
            </MotionConfig>
        </div>
    );
}

// ─── Helper para parsear las secciones de movimiento (variante 2) ─────────────
function parseMovementSections(content) {
    if (!content) return [];
    const movements = [
        "🧘 Párate, estira los brazos hacia arriba y repite en voz alta",
        "🚶 Camina 10 pasos mientras piensas en esto",
        "✋ Dibuja en el aire con tu dedo mientras lo repites",
        "💪 Haz 5 sentadillas mientras dices esto en voz alta",
    ];
    const sections = [];
    const blocks = content.split(/\n##\s+/);
    blocks.forEach((block, i) => {
        if (!block.trim() || i === 0) return;
        const lines = block.split("\n");
        const title = lines[0].replace(/^\d+\.\s*/, "").trim();
        const body = lines.slice(1).filter(l => l.trim()).slice(0, 3).join(" ").replace(/[#*`]/g, "").trim();
        if (title && body) {
            sections.push({
                title,
                summary: body.slice(0, 180) + (body.length > 180 ? "…" : ""),
                movement: movements[sections.length % movements.length],
            });
        }
    });
    return sections;
}

// ─── Variante 1: Drag & Drop emparejamiento ───────────────────────────────────
// Los pares se piden al backend (GET /ai/lessons/{id}/matching), que los genera
// como JSON estructurado una sola vez por lección y los cachea. Antes se parseaba
// el texto narrativo, lo que fallaba cuando el formato no era el esperado.
function DragMatchGame({ lessonId }) {
    const [loading, setLoading] = useState(true);
    const [pairs, setPairs] = useState([]);

    useEffect(() => {
        let active = true;
        async function load() {
            setLoading(true);
            try {
                const r = await api.get(`/ai/lessons/${lessonId}/matching`);
                if (active) setPairs(r.data?.has_matching ? (r.data.pairs || []) : []);
            } catch {
                if (active) setPairs([]);
            } finally {
                if (active) setLoading(false);
            }
        }
        load();
        return () => { active = false; };
    }, [lessonId]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
            <RefreshCw className="animate-spin text-orange-600 dark:text-orange-400" size={28} />
            <p className="text-slate-500 dark:text-slate-400 text-sm">Preparando la actividad de emparejar…</p>
        </div>
    );

    if (!pairs.length) return (
        <div className="text-center py-12">
            <p className="text-slate-500 dark:text-slate-400 text-sm">Esta lección no tiene actividad de emparejar disponible.</p>
        </div>
    );

    return <DragMatchBoard pairs={pairs} />;
}

// Tablero del juego, ya con los pares listos. Se separó para poder inicializar
// el orden aleatorio de forma limpia una vez que los pares están cargados.
function DragMatchBoard({ pairs }) {
    // Normalizamos: el backend usa "definition", el juego internamente usa "def".
    const normalized = useMemo(
        () => pairs.map((p) => ({ term: p.term, def: p.definition ?? p.def })),
        [pairs]
    );
    const [terms] = useState(() => [...normalized].sort(() => Math.random() - 0.5));
    const [defs]  = useState(() => [...normalized].sort(() => Math.random() - 0.5));
    const [matches, setMatches] = useState({});
    const [dragging, setDragging] = useState(null);
    const [checked, setChecked] = useState(false);
    const [results, setResults] = useState({});

    const usedDefs = Object.values(matches);

    function handleDrop(defIdx) {
        if (dragging?.type !== "term") return;
        setMatches(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(k => { if (next[k] === defIdx) delete next[k]; });
            next[dragging.idx] = defIdx;
            return next;
        });
        setDragging(null);
        setChecked(false);
        setResults({});
    }

    function handleCheck() {
        const res = {};
        terms.forEach((t, tIdx) => {
            const defIdx = matches[tIdx];
            if (defIdx === undefined) { res[tIdx] = "empty"; return; }
            const matched = defs[defIdx];
            const correct = normalized.find(p => p.term === t.term);
            res[tIdx] = matched.def === correct?.def ? "correct" : "wrong";
        });
        setResults(res);
        setChecked(true);
    }

    function handleReset() {
        setMatches({});
        setChecked(false);
        setResults({});
        setDragging(null);
    }

    const allMatched = Object.keys(matches).length === terms.length;
    const allCorrect = checked && Object.values(results).every(r => r === "correct");

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <h3 className="text-slate-900 dark:text-white font-semibold text-sm flex items-center gap-2">🧩 Empareja el término con su definición</h3>
                <button onClick={handleReset} className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                    <RotateCcw size={12} /> Reiniciar
                </button>
            </div>
            {allCorrect && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                    className="bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-400 dark:border-emerald-600/50 rounded-xl p-4 text-center">
                    <p className="text-emerald-700 dark:text-emerald-400 font-bold text-base">🎉 ¡Perfecto! Todo correcto</p>
                    <p className="text-emerald-600 dark:text-emerald-300 text-xs mt-1">Dominaste todos los conceptos de esta lección</p>
                </motion.div>
            )}
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                    <p className="text-[10px] text-orange-600 dark:text-orange-400 font-semibold uppercase tracking-wider text-center mb-2">Términos</p>
                    {terms.map((t, tIdx) => {
                        const status = results[tIdx];
                        return (
                            <motion.div key={tIdx} whileTap={{ scale: 0.97 }} draggable
                                onDragStart={() => setDragging({ type: "term", idx: tIdx })}
                                onDragEnd={() => setDragging(null)}
                                className={`p-3 rounded-xl text-xs font-semibold text-center cursor-grab border transition-all select-none
                                    ${status === "correct" ? "bg-emerald-100 dark:bg-emerald-800/60 border-emerald-400 dark:border-emerald-500/60 text-emerald-700 dark:text-emerald-200"
                                    : status === "wrong"   ? "bg-red-100 dark:bg-red-900/60 border-red-400 dark:border-red-500/60 text-red-700 dark:text-red-200"
                                    : dragging?.type === "term" && dragging?.idx === tIdx
                                        ? "bg-orange-500 dark:bg-orange-700/80 border-orange-400 text-white scale-95"
                                        : "bg-orange-50 dark:bg-orange-900/40 border-orange-300 dark:border-orange-700/50 text-orange-700 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-800/50"}`}>
                                {status === "correct" && <CheckCircle size={12} className="inline mr-1 text-emerald-600 dark:text-emerald-400" />}
                                {status === "wrong"   && <XCircle    size={12} className="inline mr-1 text-red-600 dark:text-red-400" />}
                                {t.term}
                            </motion.div>
                        );
                    })}
                </div>
                <div className="space-y-2">
                    <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold uppercase tracking-wider text-center mb-2">Definiciones</p>
                    {defs.map((d, dIdx) => {
                        const isMatched = usedDefs.includes(dIdx);
                        const matchedTermIdx = Object.keys(matches).find(k => matches[k] === dIdx);
                        const status = matchedTermIdx !== undefined ? results[matchedTermIdx] : null;
                        return (
                            <div key={dIdx} onDragOver={(e) => e.preventDefault()} onDrop={() => handleDrop(dIdx)}
                                className={`p-3 rounded-xl text-xs text-center border min-h-[48px] flex items-center justify-center transition-all
                                    ${status === "correct" ? "bg-emerald-100 dark:bg-emerald-800/40 border-emerald-400 dark:border-emerald-500/60 text-emerald-700 dark:text-emerald-200"
                                    : status === "wrong"   ? "bg-red-100 dark:bg-red-900/40 border-red-400 dark:border-red-500/60 text-red-700 dark:text-red-200"
                                    : isMatched ? "bg-indigo-100 dark:bg-indigo-800/50 border-indigo-400 dark:border-indigo-500/60 text-indigo-700 dark:text-indigo-100"
                                    : dragging?.type === "term" ? "bg-slate-200 dark:bg-slate-700/80 border-indigo-400/60 border-dashed text-slate-600 dark:text-slate-300"
                                    : "bg-slate-100 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600/50 text-slate-500 dark:text-slate-400"}`}>
                                {d.def}
                            </div>
                        );
                    })}
                </div>
            </div>
            <button onClick={handleCheck} disabled={!allMatched}
                className="w-full py-3 rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-30 text-white text-sm font-bold transition-all">
                Verificar respuestas ✓
            </button>
            {checked && !allCorrect && (
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                    {Object.values(results).filter(r => r === "correct").length} de {terms.length} correctos — ¡sigue intentando!
                </p>
            )}
        </div>
    );
}

// ─── Variante 2: Descanso activo ──────────────────────────────────────────────
function ActiveBreakStudy({ content }) {
    const sections = useMemo(() => parseMovementSections(content), [content]);
    const [current, setCurrent] = useState(0);
    const [done, setDone] = useState([]);

    if (!sections.length) return (
        <div className="text-center py-12">
            <p className="text-slate-500 dark:text-slate-400 text-sm">No se encontraron secciones para esta actividad.</p>
        </div>
    );

    const sec = sections[current];
    const isLast = current === sections.length - 1;
    const allDone = done.length === sections.length;

    function handleNext() {
        setDone(prev => [...new Set([...prev, current])]);
        if (!isLast) setCurrent(c => c + 1);
    }

    function handleReset() { setCurrent(0); setDone([]); }

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <h3 className="text-slate-900 dark:text-white font-semibold text-sm flex items-center gap-2">🤸 Estudia en movimiento</h3>
                <button onClick={handleReset} className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                    <RotateCcw size={12} /> Reiniciar
                </button>
            </div>
            <div className="flex gap-1.5">
                {sections.map((_, i) => (
                    <div key={i} className={`flex-1 h-1.5 rounded-full transition-all ${
                        done.includes(i) ? "bg-orange-500" : i === current ? "bg-orange-400/60" : "bg-slate-200 dark:bg-slate-700"}`} />
                ))}
            </div>
            {allDone ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-10 space-y-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-500/30 rounded-2xl">
                    <p className="text-4xl">🏆</p>
                    <p className="text-orange-600 dark:text-orange-400 font-bold text-lg">¡Completaste la sesión!</p>
                    <p className="text-slate-600 dark:text-slate-300 text-sm px-6">Estudiaste toda la lección en movimiento. Tu cuerpo y mente lo agradecen.</p>
                    <button onClick={handleReset} className="px-6 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 rounded-lg text-sm text-slate-900 dark:text-white transition-colors">
                        Volver a empezar
                    </button>
                </motion.div>
            ) : (
                <AnimatePresence mode="wait">
                    <motion.div key={current} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }} className="space-y-4">
                        <div className="bg-slate-100 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-700/50 rounded-2xl p-5">
                            <p className="text-[10px] text-orange-600 dark:text-orange-400 font-semibold uppercase tracking-wider mb-2">
                                Sección {current + 1} de {sections.length}
                            </p>
                            <h4 className="text-slate-900 dark:text-white font-bold text-sm mb-2">{sec.title}</h4>
                            <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">{sec.summary}</p>
                        </div>
                        <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-500/40 rounded-2xl p-5 text-center space-y-2">
                            <p className="text-orange-700 dark:text-orange-300 font-semibold text-sm">{sec.movement}:</p>
                            <p className="text-slate-900 dark:text-white font-bold text-base leading-snug">"{sec.title}"</p>
                            <p className="text-orange-700/70 dark:text-orange-200/70 text-xs">Hazlo 3 veces antes de continuar</p>
                        </div>
                        <button onClick={handleNext}
                            className="w-full py-3 rounded-xl bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold transition-all">
                            {isLast ? "¡Terminé! 🎉" : "Listo, siguiente sección →"}
                        </button>
                    </motion.div>
                </AnimatePresence>
            )}
        </div>
    );
}

// ─── Flashcard individual ─────────────────────────────────────────────────────
function Flashcard({ card, index, total }) {
    const [flipped, setFlipped] = useState(false);
    const categoryColors = {
        "Definición":  "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700/50",
        "Fecha":       "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700/50",
        "Personaje":   "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700/50",
        "Concepto":    "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700/50",
        "Fórmula":     "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700/50",
        "Vocabulario": "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700/50",
        "Dato clave":  "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700/50",
    };
    const colorClass = categoryColors[card.category] || "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600";
    return (
        <div className="cursor-pointer select-none" style={{ perspective: "1000px" }} onClick={() => setFlipped(!flipped)}>
            <motion.div style={{ transformStyle: "preserve-3d" }} animate={{ rotateY: flipped ? 180 : 0 }}
                transition={{ duration: 0.45, type: "spring", bounce: 0.2 }} className="relative w-full">
                <div style={{ backfaceVisibility: "hidden" }}
                    className="bg-slate-50 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-700/60 rounded-2xl p-6 shadow-lg min-h-[160px] flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-3">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${colorClass}`}>{card.category}</span>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">{index + 1}/{total}</span>
                    </div>
                    <p className="text-slate-900 dark:text-white text-sm font-medium leading-relaxed flex-1 flex items-center">{card.question}</p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-3 text-center">Toca para ver la respuesta</p>
                </div>
                <div style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                    className="absolute inset-0 bg-indigo-50 dark:bg-indigo-950/80 border border-indigo-200 dark:border-indigo-700/40 rounded-2xl p-6 shadow-lg min-h-[160px] flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold">Respuesta</span>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">{index + 1}/{total}</span>
                    </div>
                    <p className="text-indigo-900 dark:text-indigo-100 text-sm leading-relaxed flex-1 flex items-center">{card.answer}</p>
                    <p className="text-[10px] text-indigo-400 dark:text-indigo-500 mt-3 text-center">Toca para volver</p>
                </div>
            </motion.div>
        </div>
    );
}

// ─── Panel de Flashcards ──────────────────────────────────────────────────────
function FlashcardsPanel({ lessonId }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const r = await api.get(`/ai/lessons/${lessonId}/flashcards`);
                setData(r.data);
            } catch { toast.error("No se pudieron cargar las flashcards"); }
            finally { setLoading(false); }
        }
        load();
    }, [lessonId]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
            <RefreshCw className="animate-spin text-indigo-600 dark:text-indigo-400" size={28} />
            <p className="text-slate-500 dark:text-slate-400 text-sm">Generando flashcards con IA…</p>
        </div>
    );
    if (!data?.has_flashcards || !data?.cards?.length) return (
        <div className="text-center py-16">
            <p className="text-4xl mb-3">🤔</p>
            <p className="text-slate-500 dark:text-slate-400 text-sm">Este tema no requiere flashcards de memorización.</p>
            <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">{data?.reason}</p>
        </div>
    );
    const cards = data.cards;
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
                <p className="text-xs text-slate-500 dark:text-slate-400">{cards.length} flashcards</p>
                <button onClick={() => setCurrent(0)} className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                    <RotateCcw size={12} /> Reiniciar
                </button>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-1">
                <div className="bg-indigo-500 h-1 rounded-full transition-all duration-300" style={{ width: `${((current + 1) / cards.length) * 100}%` }} />
            </div>
            <Flashcard key={current} card={cards[current]} index={current} total={cards.length} />
            <div className="flex gap-3 pt-1">
                <button onClick={() => setCurrent((c) => Math.max(0, c - 1))} disabled={current === 0}
                    className="flex-1 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-medium disabled:opacity-30 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                    ← Anterior
                </button>
                <button onClick={() => setCurrent((c) => Math.min(cards.length - 1, c + 1))} disabled={current === cards.length - 1}
                    className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-medium disabled:opacity-30 hover:bg-indigo-500 transition-colors">
                    Siguiente →
                </button>
            </div>
            <details className="mt-4">
                <summary className="text-xs text-slate-400 dark:text-slate-500 cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition-colors py-2">
                    Ver todas las flashcards ({cards.length})
                </summary>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    {cards.map((card, i) => <Flashcard key={i} card={card} index={i} total={cards.length} />)}
                </div>
            </details>
        </div>
    );
}

// ─── Constantes ───────────────────────────────────────────────────────────────
const STYLES = [
    { id: 0, key: "visual",      emoji: "👁️",  label: "Visual",      desc: "Esquemas y analogías",   bubbleColor: "bg-blue-600 dark:bg-blue-700/80" },
    { id: 1, key: "auditivo",    emoji: "🎧", label: "Auditivo",    desc: "Narración y guiones",    bubbleColor: "bg-purple-600 dark:bg-purple-700/80" },
    { id: 2, key: "lector",      emoji: "📖", label: "Lector",      desc: "Definiciones técnicas",  bubbleColor: "bg-emerald-600 dark:bg-emerald-700/80" },
    { id: 3, key: "kinestesico", emoji: "🤸", label: "Kinestésico", desc: "Dinámicas prácticas",    bubbleColor: "bg-orange-600 dark:bg-orange-700/80" },
];

function renderInline(text) {
    // Maneja **negrita** y *itálica*. Antes solo reconocía la negrita doble,
    // por eso los *asteriscos simples* salían crudos en el texto.
    return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={i} className="text-slate-900 dark:text-white font-semibold">{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
            return <em key={i} className="italic">{part.slice(1, -1)}</em>;
        }
        return part;
    });
}

function SimpleMarkdown({ content }) {
    if (!content) return null;
    return (
        <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            {content.split("\n").map((line, i) => {
                if (line.startsWith("## "))  return <h2 key={i} className="text-base font-bold text-slate-900 dark:text-white mt-5 mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">{line.replace(/^## /, "")}</h2>;
                if (line.startsWith("### ")) return <h3 key={i} className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mt-3 mb-1">{line.replace(/^### /, "")}</h3>;
                if (line.startsWith("# "))   return <h1 key={i} className="text-lg font-bold text-slate-900 dark:text-white mt-5 mb-2">{line.replace(/^# /, "")}</h1>;
                if (line.startsWith("- ") || line.startsWith("* ")) return (
                    <div key={i} className="flex gap-2 ml-2">
                        <span className="text-indigo-600 dark:text-indigo-400 mt-0.5">•</span>
                        <span>{renderInline(line.replace(/^[-*] /, ""))}</span>
                    </div>
                );
                if (line.match(/^\d+\. /)) return (
                    <div key={i} className="flex gap-2 ml-2">
                        <span className="text-indigo-600 dark:text-indigo-400 font-mono text-xs mt-0.5">{line.match(/^(\d+)\./)[1]}.</span>
                        <span>{renderInline(line.replace(/^\d+\. /, ""))}</span>
                    </div>
                );
                if (line.trim() === "") return <div key={i} className="h-2" />;
                return <p key={i}>{renderInline(line)}</p>;
            })}
        </div>
    );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function LessonVersion() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const chatEndRef = useRef(null);

    const currentStyle = searchParams.get("style") || "visual";
    const [currentVariant, setCurrentVariant] = useState(1);
    const [showFlashcards, setShowFlashcards] = useState(false);

    const [lesson, setLesson]           = useState(null);
    const [loading, setLoading]         = useState(true);
    const [data, setData]               = useState(null);
    const [loadingText, setLoadingText] = useState(false);

    const [chatSession, setChatSession]   = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput]       = useState("");
    const [chatSending, setChatSending]   = useState(false);
    const [chatOpen, setChatOpen]         = useState(false);
    const [coachMode, setCoachMode]       = useState(false);
    const [checkpointMode, setCheckpointMode] = useState(false);
    const [hiddenSetup, setHiddenSetup]       = useState(0);
    const [aiCompleted, setAiCompleted]       = useState(false);
    const [completing, setCompleting]         = useState(false);

    const [speaking, setSpeaking] = useState(false);
    const utteranceRef = useRef(null);

    // Vuelve a la pantalla de detalle de la lección (/alumno/lecciones/:id).
    // Antes usaba navigate(-1), pero cada cambio de pestaña/variante metía una
    // entrada en el historial, así que "Volver" solo deshacía un cambio a la vez
    // y había que pulsarlo varias veces. Ahora navega directo al detalle.
    function handleBack() {
        navigate(`/alumno/lecciones/${id}`);
    }

    useEffect(() => {
        async function init() {
            try {
                const [ru, rl] = await Promise.all([api.get("/auth/me"), api.get(`/lessons/${id}`)]);
                setLesson(rl.data);
                setAiCompleted(!!rl.data?.ai_completed);
                if (ru.data?.learning_style && !searchParams.get("style")) {
                    // replace: true → no crea una entrada nueva en el historial
                    setSearchParams({ style: ru.data.learning_style }, { replace: true });
                }
            } catch { toast.error("Error al cargar datos"); }
            finally { setLoading(false); }
        }
        init();
    }, [id]);

    useEffect(() => {
        if (!lesson) return;
        async function loadContent() {
            setLoadingText(true);
            setData(null);
            try {
                const r = await api.get(`/ai/lessons/${id}/my-version`, {
                    params: { style: currentStyle, variant: currentVariant }
                });
                setData(r.data);
            } catch { toast.error("No se encontró versión generada"); }
            finally { setLoadingText(false); }
        }
        loadContent();
    }, [id, currentStyle, currentVariant, lesson]);

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);
    useEffect(() => () => window.speechSynthesis.cancel(), []);

    function speakContent() {
        if (!data?.content) return;
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(data.content.replace(/[#*`>-]/g, ""));
        utterance.lang = "es-ES";
        utterance.rate = 0.95;
        utterance.pitch = 1;
        utterance.onend = () => setSpeaking(false);
        utterance.onerror = () => setSpeaking(false);
        utteranceRef.current = utterance;
        setSpeaking(true);
        window.speechSynthesis.speak(utterance);
    }

    function stopSpeaking() { window.speechSynthesis.cancel(); setSpeaking(false); }

    async function sendChat(e) {
        e.preventDefault();
        if (!chatInput.trim() || chatSending) return;
        const text = chatInput.trim();
        setChatInput("");
        setChatSending(true);
        try {
            let session = chatSession;
            if (!session) {
                const { data: s } = await api.post("/ai/sessions", { lesson_id: id, kind: coachMode ? "socratico" : "tutor" });
                setChatSession(s);
                session = s;
            }
            const res = await api.post("/ai/messages", {
                session_id: session.id,
                text,
                lesson_version_key: `${currentStyle}_${currentVariant}`
            });
            setChatMessages(res.data.messages);
        } catch { toast.error("Error en el chat"); }
        finally { setChatSending(false); }
    }

    // Texto "semilla": dispara la primera pregunta del Coach. No se muestra
    // (se oculta junto con el saludo via hiddenSetup) para que el chat abra
    // directo con la pregunta que la IA genera sobre esta leccion.
    const CHECKPOINT_SEED = "Empecemos el repaso de esta leccion. Hazme una sola primera pregunta para que yo razone sobre el tema.";

    async function startCheckpoint() {
        if (aiCompleted || chatSending) return;
        setCoachMode(true);
        setCheckpointMode(true);
        setChatSession(null);
        setChatMessages([]);
        setHiddenSetup(0);
        setChatOpen(true);
        setChatSending(true);
        try {
            const { data: sess } = await api.post("/ai/sessions", { lesson_id: id, kind: "socratico" });
            setChatSession(sess);
            const res = await api.post("/ai/messages", {
                session_id: sess.id,
                text: CHECKPOINT_SEED,
                lesson_version_key: `${currentStyle}_${currentVariant}`
            });
            const msgs = res.data.messages || [];
            // Oculta todo hasta la semilla inclusive; muestra desde la 1ra pregunta real.
            const seedIdx = msgs.findIndex((m) => m.role === "user" && m.content === CHECKPOINT_SEED);
            setHiddenSetup(seedIdx >= 0 ? seedIdx + 1 : 0);
            setChatMessages(msgs);
        } catch { toast.error("No se pudo iniciar el Coach"); }
        finally { setChatSending(false); }
    }

    async function finishCheckpoint() {
        setCompleting(true);
        try {
            await api.post(`/lessons/${id}/ai-complete`);
            setAiCompleted(true);
            toast.success("¡Aprendizaje IA completado!");
            setChatOpen(false);
            setCheckpointMode(false);
            setChatMessages([]);
            setHiddenSetup(0);
        } catch { toast.error("No se pudo marcar como completado"); }
        finally { setCompleting(false); }
    }

    const visibleMessages = chatMessages.slice(hiddenSetup);
    const tabs = STYLES.map((s) => ({
        ...s,
        content: (
            <div className="bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200 dark:border-slate-700/50 p-6 shadow-xl">
                {/* Selector variante + botón flashcards */}
                <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                    <div className="flex gap-2">
                        {(s.key === "auditivo" ? [1] : [1, 2]).map((v) => (
                            <button key={v}
                                onClick={() => { setCurrentVariant(v); setShowFlashcards(false); }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                    currentVariant === v && !showFlashcards
                                        ? "bg-indigo-600 text-white shadow-lg"
                                        : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                                }`}>
                                {s.key === "visual"
                                    ? v === 1 ? "🗺️ Mapa" : "📅 Timeline"
                                    : s.key === "kinestesico"
                                        ? v === 1 ? "🧩 Emparejar" : "🤸 Movimiento"
                                        : `Variante ${v}`
                                }
                            </button>
                        ))}
                    </div>
                    {s.key === "visual" && (
                        <button onClick={() => setShowFlashcards((prev) => !prev)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                                showFlashcards
                                    ? "bg-amber-600 text-white shadow-lg"
                                    : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                            }`}>
                            🃏 Flashcards
                        </button>
                    )}
                </div>

                {/* Botón audio auditivo */}
                {s.key === "auditivo" && data?.content && !showFlashcards && (
                    <div className="flex justify-end mb-4">
                        {!speaking ? (
                            <button onClick={speakContent} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium">
                                <Volume2 size={16} /> Escuchar
                            </button>
                        ) : (
                            <button onClick={stopSpeaking} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-medium">
                                <Pause size={16} /> Detener
                            </button>
                        )}
                    </div>
                )}

                {/* Contenido */}
                {/* dataMatchesTab evita mostrar el contenido de la pestaña anterior
                    en la nueva mientras se está cargando (el bug del JSON de Visual
                    apareciendo en Lector, etc.). data.style lo devuelve el backend. */}
                {(() => {
                    const dataMatchesTab = data && (!data.style || data.style === s.key);
                    return (
                        showFlashcards ? (
                            <FlashcardsPanel lessonId={id} />
                        ) : s.key === "kinestesico" && !loadingText && dataMatchesTab && data?.content ? (
                            currentVariant === 1
                                ? <DragMatchGame key={`drag-${id}`} lessonId={id} />
                                : <ActiveBreakStudy key={`break-${id}`} content={data.content} />
                        ) : loadingText || (data && !dataMatchesTab) ? (
                            <div className="flex flex-col items-center justify-center py-20 gap-3">
                                <RefreshCw className="animate-spin text-indigo-600 dark:text-indigo-400" size={28} />
                                <p className="text-slate-500 dark:text-slate-400 text-sm">Cargando explicación personalizada…</p>
                            </div>
                        ) : !data ? (
                            <p className="text-slate-500 dark:text-slate-400 text-center py-10">Sin contenido disponible.</p>
                        ) : s.key === "visual" ? (
                            data.parsed_content
                                ? <MindMap data={data.parsed_content} />
                                : (
                                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                                        <p className="text-4xl">⚠️</p>
                                        <p className="text-slate-700 dark:text-slate-300 text-sm font-medium">El mapa no pudo renderizarse.</p>
                                        <p className="text-slate-400 dark:text-slate-500 text-xs text-center max-w-xs">Pedile al profesor que regenere las versiones de esta lección.</p>
                                    </div>
                                )
                        ) : (
                            <SimpleMarkdown content={data.content} />
                        )
                    );
                })()}
            </div>
        ),
    }));

    if (loading) return (
        <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center">
            <RefreshCw className="animate-spin text-indigo-600 dark:text-indigo-400" size={32} />
        </div>
    );

    const styleInfo = STYLES.find((s) => s.key === currentStyle);

    return (
        <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
            <div className="sticky top-0 z-20 bg-white/95 dark:bg-slate-950/95 backdrop-blur border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex items-center justify-between">
                <button onClick={handleBack} className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                    <ArrowLeft size={18} /> Volver
                </button>
                <h1 className="text-base font-semibold text-slate-900 dark:text-white truncate max-w-xs">{lesson?.title}</h1>
                <div className="flex items-center gap-2">
                    <button onClick={startCheckpoint} disabled={aiCompleted}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                            aiCompleted
                                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 cursor-default"
                                : "bg-amber-500 hover:bg-amber-400 text-white"
                        }`}>
                        <CheckCircle size={15} /> {aiCompleted ? "Lección completada" : "Completar lección"}
                    </button>
                    <button onClick={() => { setCheckpointMode(false); setHiddenSetup(0); setChatOpen(true); }}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium transition-colors">
                        <MessageSquare size={15} /> Chat de dudas
                    </button>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-8">
                <DirectionAwareTabs
                    tabs={tabs}
                    onChange={(newId) => {
                        const style = STYLES.find((s) => s.id === newId)?.key ?? "visual";
                        // replace: true → cambiar de pestaña no ensucia el historial
                        setSearchParams({ style }, { replace: true });
                        setCurrentVariant(1);
                        setShowFlashcards(false);
                        window.speechSynthesis.cancel();
                        setSpeaking(false);
                    }}
                />
            </div>

            {chatOpen && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg flex flex-col shadow-2xl" style={{ height: "520px" }}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                                    {coachMode ? "Modo Coach 🧠" : "Chat de dudas"}
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {coachMode
                                        ? "Te haré preguntas para que razones tú mismo/a"
                                        : <>{styleInfo?.emoji} {styleInfo?.label} · Variante {currentVariant}</>}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        // Cambiar de modo reinicia la conversación: la sesión se
                                        // crea con un kind distinto (tutor vs socratico) en el backend.
                                        setCoachMode((prev) => !prev);
                                        setCheckpointMode(false);
                                        setChatSession(null);
                                        setChatMessages([]);
                                        setHiddenSetup(0);
                                    }}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                        coachMode
                                            ? "bg-indigo-600 text-white hover:bg-indigo-500"
                                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                                    }`}>
                                    {coachMode ? "🧠 Coach ON" : "🧠 Modo Coach"}
                                </button>
                                <button onClick={() => setChatOpen(false)} className="text-slate-400 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white p-1">✕</button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                            {visibleMessages.length === 0 && !chatSending && (
                                <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-8">
                                    {checkpointMode
                                        ? "El Coach está preparando tu primera pregunta…"
                                        : "Preguntame cualquier duda sobre lo que estás leyendo."}
                                </p>
                            )}
                            {visibleMessages.map((m, i) => (
                                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                                        m.role === "user" ? "bg-indigo-600 text-white rounded-br-sm" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-bl-sm"
                                    }`}>
                                        {m.role === "assistant" ? <SimpleMarkdown content={m.content} /> : m.content}
                                    </div>
                                </div>
                            ))}
                            {chatSending && (
                                <div className="flex justify-start">
                                    <div className="bg-slate-100 dark:bg-slate-800 px-4 py-3 rounded-2xl rounded-bl-sm">
                                        <RefreshCw size={14} className="animate-spin text-slate-400 dark:text-slate-400" />
                                    </div>
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                        {checkpointMode && visibleMessages.filter((m) => m.role === "user").length >= 2 && (
                            <div className="px-4 pt-3">
                                <button onClick={finishCheckpoint} disabled={completing}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50">
                                    <CheckCircle size={16} /> {completing ? "Marcando…" : "Terminar y marcar como completado"}
                                </button>
                            </div>
                        )}
                        <form onSubmit={sendChat} className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                            <input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                                placeholder="Escribí tu duda…" disabled={chatSending}
                                className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-4 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500" />
                            <button type="submit" disabled={chatSending || !chatInput.trim()}
                                className="p-2.5 rounded-xl bg-indigo-600 text-white disabled:opacity-50 hover:bg-indigo-500">
                                <Send size={16} />
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
